import type { ReactNode } from "react";

import { Label } from "@/components/ui/label";

// FormField is the label + control + help/error scaffold every form uses.
// Extracted so per-entity forms don't redefine the same wrapper.

export interface FormFieldProps {
  label: string;
  help?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}

export function FormField({
  label,
  help,
  error,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={"space-y-1 " + (className ?? "")}>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {children}
      {help && !error && (
        <p className="text-xs text-muted-foreground">{help}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Normalise a form-string field to number | undefined. Blanks → undefined. */
export function parseOptNum(v: string | undefined): number | undefined {
  if (v === undefined || v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

/** Convert empty/whitespace to undefined for optional string fields. */
export function emptyToUndef(s: string | undefined): string | undefined {
  if (s === undefined) return undefined;
  const t = s.trim();
  return t.length > 0 ? t : undefined;
}

/** Format a number for an HTML input. Undefined → "". */
export function numToStr(n: number | undefined): string {
  return n === undefined ? "" : String(n);
}
